import { useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  ClipboardList,
  FolderOpen,
  Gem,
  Phone,
  SquarePen,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { queryClient } from "App";
import ACL_RELATIONSHIPS from "acl-relationships";
import CallsTable from "components/CallsTableV3/CallsTableV3";
import withRoleCheck from "components/hocs/withRoleCheck";
import _RevenueOpportunitiesTable from "components/RevenueOpportunitiesTableV3/RevenueOpportunitiesTableV3";
import { SECTION_FILL_CLASSES } from "components/sidebar/sidebarShell";
import SidebarDetailLayout from "components/SidebarDetailLayout";
import NotesView from "components/views/NotesView";
import CONFIG from "Config";
import { noTag } from "constants/Tag";
import { useAppTabs } from "hooks/useAppTabs";
import usePageTitle from "hooks/usePageTitle";
import useShowDisabledTags from "hooks/useShowDisabledTags";
import CasesTable from "routes/case/CasesTableV3";
import ContactsTable from "routes/campaign/components/campaign_contact_table/CampaignContactsTableV3";
import CampaignTargetInfoPanel from "routes/campaign/components/CampaignTargetInfoPanel";
import { EditCampaignTargetModal as _EditCampaignTargetModal } from "routes/campaign/components/Modals";
import NoticesTable from "routes/campaign/components/notices_table/NoticesTableV3";
import AuthService from "services/Auth";
import CampaignService from "services/Campaign";
import EntityService from "services/Entity";

const EditCampaignTargetModal = withRoleCheck(_EditCampaignTargetModal, [
  ACL_RELATIONSHIPS.campaignTarget.edit,
]);

const RevenueOpportunitiesTable = withRoleCheck(
  _RevenueOpportunitiesTable,
  [ACL_RELATIONSHIPS.revenueOpportunity.readTable],
  { showMsg: true }
);

const initialCampaignTarget = {
  id: null,
  entity_name: "",
  campaign_id: null,
  entity_id: null,
  campaign_status: {},
  metrics: {},
  custom_fields: {},
};

const initialEntity = {
  id: null,
  name: null,
  tags: [],
  custom_fields: {},
};

const fetchCampaignTarget = async ({ queryKey }) => {
  const [, campaignId, entityId] = queryKey;
  const target = await CampaignService.getCampaignTarget(campaignId, entityId);
  return target || initialCampaignTarget;
};

const fetchEntity = async ({ queryKey }) => {
  const [, entityId, showDisabledTags] = queryKey;
  const entity = await EntityService.getEntity(entityId, {
    show_disabled_tags: showDisabledTags,
  });
  if (entity.tags.length === 0) {
    entity.tags = [noTag];
  }
  return entity;
};

const CampaignTarget = () => {
  const { id: campaignId, entity_id: entityId } = useParams();
  const tabs = useAppTabs({
    basePath: `/campaigns/${campaignId}/target/${entityId}`,
    defaultTab: "contacts",
  });
  const showDisabledTags = useShowDisabledTags();
  const entityShowDisabledTags = showDisabledTags ? "true" : "false";

  const loggedIn = AuthService.isLoggedIn();

  const { data: campaignTarget, isFetched: isTargetFetched } = useQuery({
    queryKey: ["campaignTarget", campaignId, entityId],
    queryFn: fetchCampaignTarget,
    initialData: initialCampaignTarget,
    enabled: loggedIn && !!campaignId && !!entityId,
  });

  const { data: campaign } = useQuery({
    queryKey: ["campaign", campaignId],
    queryFn: () => CampaignService.getCampaign(campaignId),
    enabled: loggedIn && !!campaignId,
  });
  const campaignName = campaign?.name ?? "";

  const { data: entity, isFetched: isEntityFetched } = useQuery({
    queryKey: ["entity", entityId, entityShowDisabledTags],
    queryFn: fetchEntity,
    initialData: initialEntity,
    enabled: loggedIn && !!entityId,
  });

  usePageTitle(campaignTarget.entity_name || "Pleteo");

  const invalidateEntity = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["entity", entityId] }),
    [entityId]
  );

  const invalidateTarget = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["campaignTarget", campaignId, entityId],
    });
    await invalidateEntity();
  }, [campaignId, entityId, invalidateEntity]);

  const sections = useMemo(
    () => [
      {
        key: "contacts",
        label: "Contacts",
        icon: Users,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <ContactsTable
              tableName="campaign_target_contacts"
              entityId={parseInt(entityId)}
              campaignId={parseInt(campaignId)}
              entityName={entity.name}
            />
          </div>
        ),
      },
      {
        key: "notes",
        label: "Notes",
        icon: ClipboardList,
        content: (
          <NotesView modelType="CampaignTarget" modelId={campaignTarget.id} />
        ),
      },
      {
        key: "notices",
        label: "Notices",
        icon: Bell,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <NoticesTable campaignId={campaignId} entityId={entityId} />
          </div>
        ),
      },
      {
        key: "calls",
        label: "Calls",
        icon: Phone,
        hidden: !CONFIG.IS_PHONE_ENABLED,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <CallsTable campaignId={campaignId} entityId={entityId} />
          </div>
        ),
      },
      {
        key: "cases",
        label: "Cases",
        icon: FolderOpen,
        hidden: !CONFIG.ENABLE_PIRACY,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <CasesTable campaignId={campaignId} entityId={entityId} />
          </div>
        ),
      },
      {
        key: "revenueOpportunities",
        label: "Revenue Opportunities",
        icon: Gem,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <RevenueOpportunitiesTable
              campaignId={campaignId}
              entityId={entityId}
            />
          </div>
        ),
      },
    ],
    [campaignId, entityId, entity.name, campaignTarget.id]
  );

  if (!campaignTarget.id) {
    return null;
  }

  return (
    <SidebarDetailLayout
      sections={sections}
      tabs={tabs}
      header="Campaign Target"
      breadcrumb={
        <Breadcrumb>
          <BreadcrumbList className="tw:flex-nowrap tw:text-xs">
            <BreadcrumbItem className="tw:min-w-0">
              <BreadcrumbLink
                className="tw:block tw:truncate tw:text-muted-foreground! tw:hover:text-foreground!"
                render={<Link to="/campaigns" />}
              >
                All Campaigns
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="tw:[&>svg]:size-3" />
            <BreadcrumbItem className="tw:min-w-0">
              <BreadcrumbLink
                className="tw:block tw:truncate tw:text-muted-foreground! tw:hover:text-foreground!"
                render={<Link to={`/campaigns/${campaignId}`} />}
              >
                {campaignName}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="tw:[&>svg]:size-3" />
            <BreadcrumbItem className="tw:min-w-0">
              <BreadcrumbPage className="tw:block tw:truncate tw:font-semibold tw:text-foreground">
                {campaignTarget.entity_name}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      }
      title={campaignTarget.entity_name}
      titleActions={
        <>
          <EditCampaignTargetModal
            fetchCampaignTargets={invalidateTarget}
            campaignId={campaignTarget.campaign_id}
            entityId={campaignTarget.entity_id}
            trigger={
              <Button variant="outline" size="sm">
                <SquarePen data-icon="inline-start" />
                Edit
              </Button>
            }
          />
        </>
      }
      info={
        <CampaignTargetInfoPanel
          campaignTarget={campaignTarget}
          entity={entity}
          showDisabledTags={showDisabledTags}
          onUpdate={invalidateEntity}
          isLoading={!isTargetFetched}
          isEntityLoading={!!entityId && !isEntityFetched}
        />
      }
      infoLabel="Target Details"
    />
  );
};

export default CampaignTarget;
